import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Save, Download, Upload, Moon, Sun, Calendar, Target, 
  Flame, Droplets, Dumbbell, Pill, ChevronRight, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  getSettings, 
  saveSettings, 
  getDefaultSettings,
  exportAllData,
  importAllData
} from '../lib/db';
import { downloadJSON, downloadCSV } from '../lib/utils';

export const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateSetting = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    // Auto-save immediately
    try {
      await saveSettings(updated);
      toast.success('Saved!', { duration: 1000 });
    } catch (error) {
      console.error('Error saving setting:', error);
      toast.error('Failed to save');
    }
  };

  const saveAllSettings = async () => {
    try {
      await saveSettings(settings);
      setHasChanges(false);
      toast.success('Settings saved!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleExportJSON = async () => {
    try {
      const data = await exportAllData();
      downloadJSON(data, `vitality-backup-${new Date().toISOString().split('T')[0]}.json`);
      toast.success('Data exported!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Export failed');
    }
  };

  const handleExportCSV = async () => {
    try {
      const data = await exportAllData();
      // Export weight logs as CSV
      if (data.weightLogs?.length > 0) {
        downloadCSV(data.weightLogs, `vitality-weights-${new Date().toISOString().split('T')[0]}.csv`);
      }
      if (data.workoutLogs?.length > 0) {
        downloadCSV(data.workoutLogs.map(w => ({
          date: w.date,
          type: w.type,
          completed: w.completed,
          distance: w.distance || '',
          duration: w.duration || '',
        })), `vitality-workouts-${new Date().toISOString().split('T')[0]}.csv`);
      }
      toast.success('Data exported as CSV!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Export failed');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await importAllData(data);
      toast.success('Data imported successfully!');
      loadData(); // Reload settings
    } catch (error) {
      console.error('Error importing data:', error);
      toast.error('Import failed - invalid file');
    }
  };

  const resetToDefaults = async () => {
    const defaults = getDefaultSettings();
    setSettings(defaults);
    await saveSettings(defaults);
    toast.success('Settings reset to defaults');
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black tracking-tight">Settings</h1>
        <p className="text-xs text-gray-500">Changes save automatically</p>
      </div>

      {/* Program Settings */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-5 space-y-4" data-testid="program-settings">
        <h2 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
          <Calendar size={16} className="text-[#00E676]" />
          Program Settings
        </h2>
        
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wider">Start Date</Label>
            <Input
              type="date"
              value={settings.startDate}
              onChange={(e) => updateSetting('startDate', e.target.value)}
              className="mt-1 bg-[#161625] border-white/10"
              data-testid="start-date-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500 uppercase tracking-wider">Duration (days)</Label>
              <Input
                type="number"
                value={settings.durationDays}
                onChange={(e) => updateSetting('durationDays', parseInt(e.target.value) || 60)}
                className="mt-1 bg-[#161625] border-white/10 font-mono"
                data-testid="duration-input"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500 uppercase tracking-wider">Start Weight (kg)</Label>
              <Input
                type="number"
                value={settings.startWeight}
                onChange={(e) => updateSetting('startWeight', parseFloat(e.target.value) || 110)}
                className="mt-1 bg-[#161625] border-white/10 font-mono"
                data-testid="start-weight-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Target Settings */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-5 space-y-4" data-testid="target-settings">
        <h2 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
          <Target size={16} className="text-[#00E676]" />
          Daily Targets
        </h2>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <Target size={12} /> Target Weight (kg)
            </Label>
            <Input
              type="number"
              value={settings.targetWeight}
              onChange={(e) => updateSetting('targetWeight', parseFloat(e.target.value) || 90)}
              className="mt-1 bg-[#161625] border-white/10 font-mono"
              data-testid="target-weight-input"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <Flame size={12} /> Calories
            </Label>
            <Input
              type="number"
              value={settings.targetCalories}
              onChange={(e) => updateSetting('targetCalories', parseInt(e.target.value) || 1500)}
              className="mt-1 bg-[#161625] border-white/10 font-mono"
              data-testid="target-calories-input"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <Dumbbell size={12} /> Protein (g)
            </Label>
            <Input
              type="number"
              value={settings.targetProtein}
              onChange={(e) => updateSetting('targetProtein', parseInt(e.target.value) || 180)}
              className="mt-1 bg-[#161625] border-white/10 font-mono"
              data-testid="target-protein-input"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
              <Droplets size={12} /> Water (L)
            </Label>
            <Input
              type="number"
              step="0.5"
              value={settings.targetWater}
              onChange={(e) => updateSetting('targetWater', parseFloat(e.target.value) || 3.5)}
              className="mt-1 bg-[#161625] border-white/10 font-mono"
              data-testid="target-water-input"
            />
          </div>
        </div>
      </div>

      {/* Appearance */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-5" data-testid="appearance-settings">
        <h2 className="text-sm font-black uppercase tracking-wider mb-4 flex items-center gap-2">
          <Moon size={16} className="text-[#00E676]" />
          Appearance
        </h2>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.darkMode ? <Moon size={20} /> : <Sun size={20} />}
            <span className="font-medium">Dark Mode</span>
          </div>
          <Switch
            checked={settings.darkMode}
            onCheckedChange={(checked) => updateSetting('darkMode', checked)}
            data-testid="dark-mode-toggle"
          />
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-5 space-y-4" data-testid="data-settings">
        <h2 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
          <Download size={16} className="text-[#00E676]" />
          Data Management
        </h2>
        
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            onClick={handleExportJSON}
            className="border-white/10 hover:bg-white/5"
            data-testid="export-json-btn"
          >
            <Download size={16} className="mr-2" />
            Export JSON
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportCSV}
            className="border-white/10 hover:bg-white/5"
            data-testid="export-csv-btn"
          >
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>
        </div>

        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
          />
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-white/10 hover:bg-white/5"
            data-testid="import-btn"
          >
            <Upload size={16} className="mr-2" />
            Import Data
          </Button>
        </div>

        <Separator className="bg-white/5" />

        <Button 
          variant="outline" 
          onClick={resetToDefaults}
          className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
          data-testid="reset-btn"
        >
          <Trash2 size={16} className="mr-2" />
          Reset to Defaults
        </Button>
      </div>

      {/* App Info */}
      <div className="text-center text-xs text-gray-500 space-y-1">
        <p>Vitality Tracker PWA</p>
        <p>Version 1.0.0</p>
        <p>60-Day Body Transformation</p>
      </div>
    </div>
  );
};

export default Settings;
